// src/components/StatisticsPage.js
import React, { useEffect, useState } from 'react';
import { Pie, Line } from 'react-chartjs-2';
import { getExpensesByMonth, getExpensesByYear } from '../api';
import './StatisticsPage.css';
import { Link } from 'react-router-dom';
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
} from 'chart.js';

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
);

const COLORS = [
  '#FF9955',
  '#FF6666',
  '#BB11FF',
  '#0099FF',
  '#00D5B7',
  '#FFCC33',
];

async function getMonthlyDataByYear(year) {
  const response = await fetch(`/api/getMonthlyDataByYear?year=${year}`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
}

const StatisticsPage = () => {
  const [monthlyCategoryData, setMonthlyCategoryData] = useState({ income: [], expense: [] });
  const [yearlyCategoryData, setYearlyCategoryData] = useState({ income: [], expense: [] });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlyLineChartData, setYearlyLineChartData] = useState({
    income: [],
    expense: [],
  });

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear];
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const processData = (expenses) => {
    const incomeCategoryMap = {};
    const expenseCategoryMap = {};

    expenses.forEach(expense => {
      const absAmount = Math.abs(expense.amount);
      if (expense.amount >= 0) {
        incomeCategoryMap[expense.category] = (incomeCategoryMap[expense.category] || 0) + absAmount;
      } else {
        expenseCategoryMap[expense.category] = (expenseCategoryMap[expense.category] || 0) + absAmount;
      }
    });

    const incomeCategoryData = Object.entries(incomeCategoryMap)
      .map(([name, value]) => ({ name, value }));
    const expenseCategoryData = Object.entries(expenseCategoryMap)
      .map(([name, value]) => ({ name, value }));

    return { incomeCategoryData, expenseCategoryData };
  };

  useEffect(() => {
    const fetchMonthlyData = async () => {
      const data = await getExpensesByMonth(selectedMonth, selectedYear);
      const { incomeCategoryData, expenseCategoryData } = processData(data);
      setMonthlyCategoryData({ income: incomeCategoryData, expense: expenseCategoryData });
    };

    const fetchYearlyData = async () => {
      const yearlyData = await getExpensesByYear(selectedYear);
      const { incomeCategoryData, expenseCategoryData } = processData(yearlyData);
      setYearlyCategoryData({
        income: incomeCategoryData,
        expense: expenseCategoryData,
      });

      const monthlyData = await getMonthlyDataByYear(selectedYear);
      console.log("Monthly data from API:", monthlyData);
      setYearlyLineChartData(processLineChartData(monthlyData));
    };

    fetchMonthlyData();
    fetchYearlyData();
  }, [selectedMonth, selectedYear]);

  const handleMonthChange = (event) => {
    setSelectedMonth(parseInt(event.target.value, 10));
  };

  const handleYearChange = (event) => {
    setSelectedYear(parseInt(event.target.value, 10));
  };

const renderPieChart = (data, title) => {
    const chartData = {
        labels: data.map(item => item.name),
        datasets: [{
            data: data.map(item => item.value),
            backgroundColor: COLORS,
        }]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'right',
                align: 'end',
                labels: {
                    boxWidth: 30,
                    boxHeight: 10,
                    font: {
                        size: 12,
                        weight: 'normal',
                        color: '#7b5b00'
                    },
                },
            },
            tooltip: {
                callbacks: {
                    label: function(tooltipItem) {
                        return `${tooltipItem.label}: ${tooltipItem.raw}`;
                    }
                }
            }
        }
    };

    return (
        <div className="chart-container">
            <h2>{title}</h2>
            <Pie data={chartData} options={options} />
        </div>
    );
};

const processLineChartData = (monthlyData) => {
  const monthlyIncome = monthlyData.map((monthData) => monthData.income);
  const monthlyExpense = monthlyData.map((monthData) => monthData.expense);

  return { income: monthlyIncome, expense: monthlyExpense };
};

const renderLineChart = () => {
  const data = {
    labels: months.map((month) => `${month}月`),
    datasets: [
      {
        label: '收入',
        data: yearlyLineChartData.income,
        borderColor: 'green',
        backgroundColor: 'rgba(0, 255, 0, 0.5)',
        tension: 0.1,
      },
      {
        label: '支出',
        data: yearlyLineChartData.expense,
        borderColor: 'red',
        backgroundColor: 'rgba(255, 0, 0, 0.5)',
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top',
      },
      title: {
        display: true,
        text: `${selectedYear}年收支统计`,
      },
    },
    elements: {
      line: {
        tension: 0.5,
        borderWidth: 2,
        borderCapStyle: 'round',
        borderDash: [],
        borderJoinStyle: 'miter',
        capBezierPoints: true,
        fill: false,
      },
      point: {
        radius: 3,
        pointStyle: 'circle',
        borderWidth: 1,
        hitRadius: 20,
        hoverRadius: 5,
        hoverBorderWidth: 1,
      },
    },
  };

  return (
    <div className="line-chart-container">
      <h2>{selectedYear}年收支走势</h2>
      <Line data={data} options={options} />
    </div>
  );
};

  return (
    <div className="statistics-page">
      <div className="page-header">
        <Link to="/dashboard" className="back-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#00aaff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h2 className="section-title">全部收支记录</h2>
      </div>

      <div className="selectors">
        <div className="selector">
          <label htmlFor="year-selector">年份：</label>
          <select id="year-selector" value={selectedYear} onChange={handleYearChange}>
            {years.map((year) => (
              <option key={year} value={year}>
                {year}年
              </option>
            ))}
          </select>
        </div>
      </div>
      {renderLineChart()}
      <div className="charts-row">

        {renderPieChart(yearlyCategoryData.income, `${selectedYear}年收入`)}
        {renderPieChart(yearlyCategoryData.expense, `${selectedYear}年支出`)}
      </div>

      <div className="selectors">
        <div className="selector">
          <label htmlFor="month-selector">月份：</label>
          <select id="month-selector" value={selectedMonth} onChange={handleMonthChange}>
            {months.map((month) => (
              <option key={month} value={month}>
                {month}月
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="charts-row">
        {renderPieChart(monthlyCategoryData.income, `${selectedYear}年${selectedMonth}月收入`)}
        {renderPieChart(monthlyCategoryData.expense, `${selectedYear}年${selectedMonth}月支出`)}
      </div>
    </div>
  );
};

export default StatisticsPage;
